import type {
  OrderDetailsOrderLine,
  OrderDisplayLinesInput,
} from '@/types/orderDetailsOrder';
import type { ProductTypeOptions } from '@/types/product';

import {
  APOLLO_CLIENTS,
  initializeApollo,
  initializeSecureApolloClient,
} from '@/graphql/client';
import type {
  OrderAppError,
  OrderAppGiftMetadataDeleteMutation,
  OrderAppGiftMetadataDeleteMutationVariables,
  OrderAppGiftMetadataUpdateMutation,
  OrderAppGiftMetadataUpdateMutationVariables,
  OrderAppOrderGwpUpdateMutation,
  OrderAppOrderGwpUpdateMutationVariables,
  OrderAppOrderPayPerLinkTokenCreateMutation,
  OrderAppOrderPayPerLinkTokenCreateMutationVariables,
  OrderAppOrderShippingAddressUpdateMutation,
  OrderAppOrderShippingAddressUpdateMutationVariables,
  OrderAppOrderStatus,
  OrderAppPayPerLinkOrderExtraDataQuery,
  OrderAppPayPerLinkOrderExtraDataQueryVariables,
} from '@/graphql/orderApp/api';
import {
  GiftMetadataDeleteDocument,
  GiftMetadataUpdateDocument,
  OrderGwpUpdateDocument,
  OrderPayPerLinkTokenCreateDocument,
  OrderShippingAddressUpdateDocument,
  PayPerLinkOrderExtraDataDocument,
} from '@/graphql/orderApp/api';
import type {
  CheckoutFragment,
  CheckoutLineFragment,
  LanguageCodeEnum,
  Maybe,
  MyOrderDetailsQuery,
  MyOrderDetailsQueryVariables,
  OrderError,
  OrderFragment,
  OrderQuery,
  OrderQueryVariables,
  OrderUpdateMutation,
  OrderUpdateMutationVariables,
  PayPerLinkOrderDataQuery,
  PayPerLinkOrderDataQueryVariables,
  TaxedMoneyFragment,
  TransactionItemFragment,
} from '@/graphql/saleor/api';
import {
  MyOrderDetailsDocument,
  OrderDocument,
  OrderUpdateDocument,
  PayPerLinkOrderDataDocument,
} from '@/graphql/saleor/api';
import {
  ADYEN_API_URI,
  NEXT_PUBLIC_IMAGES_FORMAT,
  ProductType,
  THUMBNAIL_IMAGE_SIZE,
  VOUCHER_TYPE,
} from '@/lib/const';
import { retrievePaymentData } from '@/lib/payment/api';
import { getProductType } from '@/lib/product';
import { isOrderForCurrentStorefront } from '@/lib/storefrontBrand';

import { captureError } from './errors';
import type { PaymentType } from './payment/types';
import { checkIfVatIncluded } from './regions';

export type { OrderDisplayLinesInput } from '@/types/orderDetailsOrder';

/**
 * Only orders with pending payment status should be allowed to take payments.
 */
export const isPayPerOrderAccessible = (order: OrderFragment) => {
  return ['PENDING', 'PARTIALLY_PAID']?.includes(
    order.orderStatus as OrderAppOrderStatus,
  );
};
export const isColoradoBillingTypeForOrder = (
  order: OrderDisplayLinesInput,
) => {
  const lines =
    order?.lines?.filter((item) => item?.variant?.sku === 'colorado-dummy') ||
    [];
  if (lines.length > 0) {
    return true;
  }
  return false;
};
export const isColoradoBillingTypeForCheckout = (order: CheckoutFragment) => {
  const lines =
    order?.lines?.filter(
      (item: CheckoutLineFragment) => item.variant?.sku === 'colorado-dummy',
    ) || [];
  if (lines.length > 0) {
    return true;
  }
  return false;
};
export const excludeOrderType = (
  productType: string,
  excludedProductTypeSlug: ProductTypeOptions,
) => productType !== excludedProductTypeSlug;

const NON_DISPLAY_ORDER_LINE_SLUGS = new Set<string>([
  ProductType.ColoradoTaxDummy,
]);

export function getOrderDisplayLinesFromOrder(
  order: OrderDisplayLinesInput,
): OrderDetailsOrderLine[] {
  const lines = order.lines ?? [];
  return lines.filter((line): line is OrderDetailsOrderLine => {
    if (!line) {
      return false;
    }
    const slug = getProductType(line.variant?.product?.productType);
    if (!slug) {
      return true;
    }
    return !NON_DISPLAY_ORDER_LINE_SLUGS.has(slug);
  });
}

export const createPayPerLink = async <
  T = {
    errors: OrderAppError[];
    order: NonNullable<PayPerLinkOrderDataQuery['order']> | null;
  },
>(
  orderId: string,
): Promise<T> => {
  const client = initializeSecureApolloClient();

  {
    const { data } = await client.mutate<
      OrderAppOrderPayPerLinkTokenCreateMutation,
      OrderAppOrderPayPerLinkTokenCreateMutationVariables
    >({
      mutation: OrderPayPerLinkTokenCreateDocument,
      variables: {
        id: orderId,
        invitationMethod: 'MAIL',
      },
      context: {
        client: APOLLO_CLIENTS.orderApp,
      },
    });
    const errors = data?.orderSendPaymentInvitation?.errors ?? [];

    if (errors.length) {
      return { errors, order: null } as T;
    }
  }

  const { data } = await client.query<
    PayPerLinkOrderDataQuery,
    PayPerLinkOrderDataQueryVariables
  >({
    query: PayPerLinkOrderDataDocument,
    variables: { id: orderId },
  });

  return {
    errors: [],
    order: data.order,
  } as T;
};

export const fetchPayPerLinkOrder = async (
  token: string,
  languageCode: LanguageCodeEnum,
) => {
  const client = initializeApollo();

  const res = await client.query<
    OrderAppPayPerLinkOrderExtraDataQuery,
    OrderAppPayPerLinkOrderExtraDataQueryVariables
  >({
    fetchPolicy: 'no-cache',
    query: PayPerLinkOrderExtraDataDocument,
    variables: {
      token,
    },
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  const data = res?.data;
  const orderExtraData = data?.orderByPaymentToken;
  const isPayPerLinkTokenExpired =
    (res?.errors?.findIndex(
      (item) => item.message?.includes('Pay per link token is expired'), // will optimize after api is updated (error code missing)
    ) ?? -1) !== -1;
  if (orderExtraData) {
    const [{ data }, paymentData] = await Promise.all([
      client.query<OrderQuery, OrderQueryVariables>({
        fetchPolicy: 'no-cache',
        query: OrderDocument,
        variables: {
          id: orderExtraData.id,
          languageCode,
          thumbnailSize: THUMBNAIL_IMAGE_SIZE,
          imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
        },
      }),
      retrievePaymentData({ orderId: orderExtraData.id }),
    ]);
    const order = data?.order;

    if (order) {
      return {
        data: {
          order,
          orderExtraData,
          paymentData,
        },
        isPayPerLinkTokenExpired,
      };
    }
  }

  return { data: null, isPayPerLinkTokenExpired };
};

export const fetchOrderData = async (
  id: string,
  languageCode: LanguageCodeEnum,
) => {
  const client = initializeApollo();

  const result = await client.query<OrderQuery, OrderQueryVariables>({
    fetchPolicy: 'no-cache',
    query: OrderDocument,
    variables: {
      languageCode,
      id,
      thumbnailSize: THUMBNAIL_IMAGE_SIZE,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
    },
  });
  return { errors: result?.errors || [], data: result?.data || [] };
};

export const orderGwpUpdate = async (
  variables: OrderAppOrderGwpUpdateMutationVariables,
): Promise<OrderAppError[]> => {
  const client = initializeSecureApolloClient();

  const { data } = await client.mutate<
    OrderAppOrderGwpUpdateMutation,
    OrderAppOrderGwpUpdateMutationVariables
  >({
    mutation: OrderGwpUpdateDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return data?.gwpOrderUpdate?.errors ?? [];
};

export const orderGiftUpdate = async (
  variables: OrderAppGiftMetadataUpdateMutationVariables,
): Promise<{
  errors: OrderAppError[];
}> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppGiftMetadataUpdateMutation,
    OrderAppGiftMetadataUpdateMutationVariables
  >({
    mutation: GiftMetadataUpdateDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return {
    errors: data?.updateMetadata?.errors ?? [],
  };
};

export const orderGiftDelete = async (
  variables: OrderAppGiftMetadataDeleteMutationVariables,
): Promise<{
  errors: OrderAppError[];
}> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppGiftMetadataDeleteMutation,
    OrderAppGiftMetadataDeleteMutationVariables
  >({
    mutation: GiftMetadataDeleteDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return {
    errors: data?.deleteMetadata?.errors ?? [],
  };
};

export const orderUpdate = async (
  variables: OrderUpdateMutationVariables,
): Promise<{
  errors: Partial<OrderError>[];
  order: OrderFragment | null;
}> => {
  const client = initializeSecureApolloClient();

  const { data } = await client.mutate<
    OrderUpdateMutation,
    OrderUpdateMutationVariables
  >({
    mutation: OrderUpdateDocument,
    variables,
  });

  return {
    order: data?.orderUpdate?.order as OrderFragment | null,
    errors: data?.orderUpdate?.errors ?? [],
  };
};

export const orderShippingAddressUpdate = async (
  variables: OrderAppOrderShippingAddressUpdateMutationVariables,
): Promise<{
  errors: OrderAppError[];
}> => {
  const client = initializeSecureApolloClient();

  const { data } = await client.mutate<
    OrderAppOrderShippingAddressUpdateMutation,
    OrderAppOrderShippingAddressUpdateMutationVariables
  >({
    mutation: OrderShippingAddressUpdateDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return {
    errors: data?.orderShippingAddressUpdate?.errors ?? [],
  };
};

export type PaymentMethodName =
  | '4GIFT'
  | 'AFFIRM'
  | 'AGOS'
  | 'ALIPAY'
  | 'AMEX'
  | 'APPLEPAY'
  | 'APPLEPAY_EXPRESS'
  | 'BANCONTACT'
  | 'BANK TRANSFER'
  | 'BOB_FINANCE'
  | 'CARTEBANCAIRE'
  | 'CEMBRA_INSTALLMENT'
  | 'CEMBRA_INVOICE'
  | 'CREDIT_PLUS'
  | 'CUP'
  | 'DINERS'
  | 'DISCOVER'
  | 'GLOBAL-E'
  | 'GOOGLEPAY'
  | 'IDEAL'
  | 'JCB'
  | 'KLARNA'
  | 'MC'
  | 'OFFLINE_BANKTRANSFER'
  | 'ONEY'
  | 'ORICO_FINANCING'
  | 'PAYBYBANK'
  | 'PAYPAL'
  | 'PAYPAL_EXPRESS'
  | 'TWINT'
  | 'V12'
  | 'VISA'
  | 'ZIP'
  | 'DIRECTEBANKING'
  | 'ELO'
  | 'SMBC';

const paymentMethodNameAliasMap: Record<string, string[]> = {
  APPLEPAY: [
    'AMEX_APPLEPAY',
    'CARTEBANCAIRE_APPLEPAY',
    'DISCOVER_APPLEPAY',
    'JCB_APPLEPAY',
    'MC_APPLEPAY',
    'VISA_APPLEPAY',
    'APPLEPAY',
  ],
  APPLEPAY_EXPRESS: [
    'AMEX_APPLEPAY_EXPRESS',
    'CARTEBANCAIRE_APPLEPAY_EXPRESS',
    'DISCOVER_APPLEPAY_EXPRESS',
    'JCB_APPLEPAY_EXPRESS',
    'MC_APPLEPAY_EXPRESS',
    'VISA_APPLEPAY_EXPRESS',
    'APPLEPAY_EXPRESS',
  ],
  'GLOBAL-E': [
    'GLOBAL-E (AMAZON PAY)',
    'GLOBAL-E (AMERICAN EXPRESS)',
    'GLOBAL-E (APPLE PAY EXPRESS)',
    'GLOBAL-E (APPLE PAY)',
    'GLOBAL-E (BANK TRANSFER)',
    'GLOBAL-E (COUPONPAYMENT)',
    'GLOBAL-E (DINERS)',
    'GLOBAL-E (EFT)',
    'GLOBAL-E (GOOGLE PAY)',
    'GLOBAL-E (GRABPAY_MY)',
    'GLOBAL-E (JCB)',
    'GLOBAL-E (KLARNA)',
    'GLOBAL-E (MASTERCARD)',
    'GLOBAL-E (MB WAY)',
    'GLOBAL-E (MULTIBANCO)',
    'GLOBAL-E (PAYPAL)',
    'GLOBAL-E (PRZELEWY24)',
    'GLOBAL-E (VIPPS)',
    'GLOBAL-E (VISA)',
  ],
  GOOGLEPAY: [
    'AMEX_GOOGLEPAY',
    'AMEX_GOOGLEPAY_GOOGLEPAY',
    'VISA_GOOGLEPAY',
    'DISCOVER_GOOGLEPAY',
    'MC_GOOGLEPAY',
    'MC_GOOGLEPAY_GOOGLEPAY',
    'VISA_GOOGLEPAY_GOOGLEPAY',
    'GOOGLEPAY',
    'PAYWITHGOOGLE',
    'PAYWITHGOOGLE_PAYWITHGOOGLE',
    'PAYWITHGOOGLE_PAYWITHGOOGLE_PA',
  ],
  KLARNA: ['KLARNA', 'KLARNA_ACCOUNT', 'KLARNA_PAYNOW'],
  ONEY: ['FACILYPAY_3X', 'FACILYPAY_4X', 'ONEY'],
  PAYBYBANK: ['PAYBYBANK', 'PAYBYBANK_AIS_DD'],
  BANCONTACT: ['BCMC', 'BANCONTACT'],
};

function formatPaymentMethodName(
  paymentMethod: string,
  orderSource?: string | null,
) {
  let name = paymentMethod;

  for (const key in paymentMethodNameAliasMap) {
    const value = paymentMethodNameAliasMap[key];

    if (value.includes(paymentMethod.toUpperCase())) {
      name = key;
      break;
    }
  }
  if (orderSource === 'FOR_GIFT') {
    return `4Gift`;
  }

  return name.toUpperCase();
}

const friendlyPaymentNameMap: Record<string, string> = {
  '4GIFT': '4Gift',
  AFFIRM: 'Affirm',
  AGOS: 'Agos',
  ALIPAY: 'Alipay',
  APPLEPAY: 'Apple Pay',
  APPLEPAY_EXPRESS: 'Apple Pay Express',
  BCMC: 'Bancontact',
  BANCONTACT: 'Bancontact',
  'BANK TRANSFER': 'Bank transfer',
  CARD: 'Card',
  CEMBRA_INSTALLMENT: 'Cembra Pay',
  CEMBRA_INVOICE: 'Cembra Pay',
  CEMBRA: 'Cembra Pay',
  CREDITPLUS: 'Credit Plus',
  CREDIT_PLUS: 'Credit Plus',
  FACILYPAY_3X: 'Oney',
  FACILYPAY_4X: 'Oney',
  GOOGLEPAY: 'Google Pay',
  IDEAL: 'Ideal',
  KLARNA: 'Klarna',
  KLARNA_PAYNOW: 'Klarna',
  MC: 'Mastercard',
  OFFLINE_BANKTRANSFER: 'Bank transfer',
  ONEY: 'Oney',
  ORICO_FINANCING: 'Orico Financing',
  PAYBYBANK: 'Pay by Bank',
  PAYBYBANK_AIS_DD: 'Pay by Bank',
  PAYPAL: 'PayPal',
  PAYPAL_EXPRESS: 'PayPal Express',
  PAYWITHGOOGLE: 'Google Pay',
  SCHEME: 'Credit Card',
  TWINT: 'Twint',
  UNIONPAY: 'UnionPay',
  V12: 'V12',
  VISA: 'VISA',
  ZIP: 'ZIP',
  SMBC: 'SMBC',
};

export const getFriendlyPaymentName = (
  name: string,
  type?: PaymentType | null,
) => {
  const nameToUpper = name.toUpperCase();
  const typeToUpper = type?.toUpperCase() ?? '';
  return (
    friendlyPaymentNameMap[nameToUpper] ||
    friendlyPaymentNameMap[typeToUpper] ||
    name ||
    type ||
    ''
  );
};

const extractPaymentMethodName = (metadata?: string | null): string => {
  if (!metadata) return '';
  try {
    return JSON.parse(metadata)?.payload?.payment_method_name ?? '';
  } catch {
    return '';
  }
};
export const getPaymentMethodName = (
  transaction: TransactionItemFragment,
  orderSource?: string | null,
): string | undefined => {
  // Try to get payment method from Adyen metadata first
  let paymentMetadataValue = extractPaymentMethodName(transaction.paymentData);

  // If no Adyen payment method found, try other sources
  if (!paymentMetadataValue) {
    const adyenPaymentMethod = transaction.paymentMethod;

    if (adyenPaymentMethod && adyenPaymentMethod !== 'scheme') {
      paymentMetadataValue = adyenPaymentMethod.toUpperCase();
    } else {
      // Try 4Gift payment method
      paymentMetadataValue = extractPaymentMethodName(
        transaction.forGiftPaymentData,
      ).toUpperCase();

      // Try Orico financing payment method if still no value
      if (!paymentMetadataValue) {
        paymentMetadataValue = extractPaymentMethodName(
          transaction.oricoFinancingAppPaymentData,
        ).toUpperCase();
      }
      // try GENERIC_PAYMENT_DATA
      if (!paymentMetadataValue) {
        paymentMetadataValue = extractPaymentMethodName(
          transaction.genericPaymentData,
        ).toUpperCase();
      }
    }
  }

  if (paymentMetadataValue) {
    return (
      formatPaymentMethodName(paymentMetadataValue, orderSource) ??
      paymentMetadataValue
    );
  }
};

export const abandonPayment = async ({
  transaction_id,
}: {
  transaction_id?: string;
}): Promise<boolean> => {
  if (!ADYEN_API_URI || !transaction_id) {
    return false;
  }

  try {
    const res = await fetch(`${ADYEN_API_URI}payment/abandon`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ transaction_id }),
    });

    if (!res.ok) {
      return false;
    }

    return true;
  } catch (e) {
    return false;
  }
};

const getTaxedLineDisplayAmount = (
  price: TaxedMoneyFragment,
  channel?: string,
): number => {
  if (channel === undefined) {
    return price.net.amount;
  }
  return checkIfVatIncluded(channel) ? price.gross.amount : price.net.amount;
};

export const checkIsProductLineFree = (
  voucherType?: string,
  price?: Maybe<TaxedMoneyFragment>,
  channel?: string,
) => {
  if (voucherType !== VOUCHER_TYPE.PERCENTAGE || !price) {
    return false;
  }
  return getTaxedLineDisplayAmount(price, channel) === 0;
};

export const fetchOrderDataByOrderId = async (
  orderId: string,
  languageCode: LanguageCodeEnum,
) => {
  const client = initializeSecureApolloClient();
  try {
    const { data } = await client.query<
      MyOrderDetailsQuery,
      MyOrderDetailsQueryVariables
    >({
      query: MyOrderDetailsDocument,
      fetchPolicy: 'no-cache',
      variables: {
        id: orderId,
        thumbnailSize: THUMBNAIL_IMAGE_SIZE,
        languageCode,
      },
    });

    if (!data.order) {
      return {
        success: false,
        errorCode: 'NO_MATCH',
      };
    }

    if (!isOrderForCurrentStorefront(data.order.metadata ?? [])) {
      return {
        success: false,
        errorCode: 'NO_MATCH',
      };
    }

    const paymentMethod = await retrievePaymentData({ orderId });

    if (!paymentMethod) {
      return {
        success: false,
        errorCode: 'NO_MATCH',
      };
    }

    return {
      success: true,
      data,
      paymentMethod,
    };
  } catch (error) {
    captureError(error as Error, {
      extra: {
        error,
        orderId,
      },
    });
    return {
      success: false,
      errorCode: 'FETCH_ERROR',
    };
  }
};
